import { conditionNumericMap, gradeNumericMap, fieldMappings } from '../common/mappings.js';
import { useCompStore, usePersistedDataStore } from '../components/store.js';
import { getParcelAdjustments } from './getParcelAdjustments.js';
import logger from './logger';
import { normalizeCompId } from '../components/CaseReview/common/compSheetLookup.js';

/**
 * Unified adjustment calculation utility for comp price adjustments
 * This function calculates both raw property adjustments and misc adjustments
 * and can be used by both the renderCell logic and average calculations
 * 
 * Supports both data structures:
 * - Array-based: memoizedCompObject (used in CaseReview renderCell)
 * - Object-based: regularComps (used in TableCustomToolbar)
 */

// Fields that should be normalized to 0 when blank (boolean-like fields)
// These fields are treated as "has/doesn't have" rather than missing data
const NORMALIZE_TO_ZERO_FIELDS = [
  'InGroundPool',
  'Fireplaces', 
  'Deck',
  'Patio',
  'Porch',
  'HalfBath',
];

// Fields that contain "pool" in their name (case-insensitive)
const POOL_RELATED_FIELDS = ['pool'];

// Create reverse mapping from UI field names back to database field names
const reverseFieldMappings = Object.entries(fieldMappings).reduce((acc, [dbField, uiField]) => {
  acc[uiField] = dbField;
  return acc;
}, {});

// Helper function to get the UI field name from database field name
const getUiFieldName = (dbFieldName) => {
  return fieldMappings[dbFieldName] || dbFieldName;
};

// Helper function to check if a field should be normalized to 0 when blank
const shouldNormalizeToZero = (key) => {
  // Check exact matches first
  if (NORMALIZE_TO_ZERO_FIELDS.includes(key)) {
    return true;
  }
  
  // Check if it's a pool-related field
  if (POOL_RELATED_FIELDS.some(poolField => key.toLowerCase().includes(poolField))) {
    return true;
  }
  
  return false;
};

export function calculateCompAdjustedPrice({
  subject,
  comp,
  salePrice,
  miscAdjustments = {},
  savedComps = [],
  currentCompId = null,
  basePrice = null,
  // New parameters for array-based data structure
  memoizedCompObject = null,
  parcelIndex = null,
  // New parameters for tooltip data
  includeTooltipData = false,
  // New parameter for logging source
  logType = 'unknown',
  // New parameter to optionally exclude misc adjustments
  excludeMiscAdjustments = false,
  timeAdjustmentMonths = undefined,
}) {
  
  const shouldLog = false;
  const storeAdjustments = useCompStore.getState().adjustments || {};
  const subjectID = memoizedCompObject?.parcel_id?.[0];
  const adjustments = getParcelAdjustments(subjectID) || storeAdjustments;
  
  // New variable to control whether to use best comparable value when subject value is missing
  const UseBestCompInventory = false; // Set to true to enable using best comp value for missing subject values
  
  if (shouldLog) {
    logger.log(`=== ADJUSTMENT CALCULATOR DEBUG (${logType}) ===`);
    logger.log('Parcel ID:', currentCompId);
    logger.log('Base Sale Price:', basePrice);
    logger.log('Available Adjustments:', Object.keys(adjustments || {}));
  }
  
  // Use provided basePrice or fall back to salePrice
  let baseValue = basePrice !== null ? basePrice : (salePrice || 0);
  let totalRawAdjustment = 0;
  let totalMiscAdjustment = 0;
  let rawAdjustments = {}; // For tooltip display
  let miscAdjustmentTooltips = []; // For tooltip display
  
  if (shouldLog) {
    logger.log('Starting baseValue:', baseValue);
  }

  // Determine data structure and get subject/comp values
  const isArrayBased = memoizedCompObject && parcelIndex !== null;
  
  const getSubjectValue = (key) => {
    if (isArrayBased) {
      return memoizedCompObject[key]?.[0]; // First element is subject
    }
    // For object-based data, check both database field name and UI field name
    const uiFieldName = getUiFieldName(key);
    return subject?.[key] !== undefined ? subject?.[key] : subject?.[uiFieldName];
  };

  // Helper function to get the best (highest) comparable value for a given key
  const getBestCompValue = (key) => {
    if (isArrayBased) {
      // For array-based data, find the highest value among all comps (excluding subject at index 0)
      const values = memoizedCompObject[key]?.slice(1) || [];
      const numericValues = values
        .map(val => parseFloat(val))
        .filter(val => !isNaN(val) && val !== null && val !== undefined && val !== '');
      return numericValues.length > 0 ? Math.max(...numericValues) : null;
    } else {
      // For object-based data, we only have one comp, so return its value
      const uiFieldName = getUiFieldName(key);
      const compValue = comp?.[key] !== undefined ? comp?.[key] : comp?.[uiFieldName];
      const numericValue = parseFloat(compValue);
      return !isNaN(numericValue) && numericValue !== null && numericValue !== undefined && numericValue !== '' ? numericValue : null;
    }
  };

  const getCompValue = (key) => {
    if (isArrayBased) {
      return memoizedCompObject[key]?.[parcelIndex]; // Index-based for comp
    }
    // For object-based data, use the same mapping logic as createRows
    const uiFieldName = getUiFieldName(key);
    
    return comp?.[key] !== undefined ? comp?.[key] : comp?.[uiFieldName];
  };

  // Calculate raw property adjustments (Condition, Grade, Sqft, etc.)
  if (shouldLog) {
    logger.log('Starting raw property adjustments calculation...');
    logger.log('adjustments keys:', Object.keys(adjustments));
  }
  if (adjustments) {
    // Only iterate over keys that have valid adjustment factors
    Object.entries(adjustments).forEach(([key, adjustmentFactor]) => {
      if (shouldLog) {
        logger.log(`\n--- Processing adjustment: ${key} ---`);
      }
      
      // Skip if not a valid adjustment, if miscellaneous, or if kitchen-related
      if (adjustmentFactor === null || 
          key === 'manuallyAddedComps' || 
          key === 'specificPid' 
          // || key.toLowerCase().includes('kitchen')
        ) {
        if (shouldLog) {
          logger.log(`❌ SKIPPING ${key}: invalid adjustment factor or kitchen-related`);
        }
        return; // Skip without logging
      }
      
      const subjectValue = getSubjectValue(key);
      const compValue = getCompValue(key);
      
      // Use best comparable value if subject value is missing and UseBestCompInventory is enabled
      let finalSubjectValue = subjectValue;
      if (UseBestCompInventory && (subjectValue === null || subjectValue === undefined || subjectValue === '')) {
        const bestCompValue = getBestCompValue(key);
        if (bestCompValue !== null) {
          finalSubjectValue = bestCompValue;
          if (shouldLog) {
            logger.log(`${key}: Subject value missing (${subjectValue}), using best comp value: ${bestCompValue}`);
          }
        }
      }
      
      if (shouldLog && (key === 'Bath' || key === 'HalfBath')) {
        const uiFieldName = getUiFieldName(key);
        logger.log(`${key} (UI: ${uiFieldName}) - subjectValue:`, finalSubjectValue, 'compValue:', compValue);
        // if (!isArrayBased) {
        //   logger.log(`  Subject keys available:`, Object.keys(subject || {}));
        //   logger.log(`  Comp keys available:`, Object.keys(comp || {}));
        // }
      }
      
      // Skip if values don't exist
      if (finalSubjectValue === undefined || compValue === undefined) {
        if (shouldLog) {
          logger.log(`❌ SKIPPING ${key}: undefined values - subjectValue: ${finalSubjectValue}, compValue: ${compValue}`);
        }
        return;
      }
      
      // Special handling for Condition and Grade (percentage-based adjustments)
      if (key === 'ConditionIn' || key === 'Grade') {
        // Skip if values are completely empty
        if (finalSubjectValue === '' || compValue === '') {
          if (shouldLog) {
            logger.log(`❌ SKIPPING ${key}: empty values - subjectValue: "${finalSubjectValue}", compValue: "${compValue}"`);
          }
          return;
        }
        
        // Use the existing numeric maps for Condition and Grade calculations
        const numericMap = key === 'ConditionIn' ? conditionNumericMap : gradeNumericMap;
        
        // Handle "Unknown" values by treating them as "Average" (value 3)
        // This is the correct assumption: unknown condition/grade should be treated as average
        const subjectNumeric = numericMap[finalSubjectValue] || (finalSubjectValue === 'Unknown' ? 3 : 0);
        const compNumeric = numericMap[compValue] || (compValue === 'Unknown' ? 3 : 0);
        
        const numericDifference = subjectNumeric - compNumeric;
        const adjustmentPercentage = parseFloat(adjustmentFactor) || 0;
        
        // Calculate adjustment based on difference and percentage
        const adjustment = Math.round(salePrice * (adjustmentPercentage * numericDifference));
        totalRawAdjustment += adjustment;
        
        if (shouldLog) {
          if (adjustment === 0) {
            logger.log(`⚪ ${key} ADJUSTMENT (ZERO - PERCENTAGE-BASED):`, {
              subjectValue: finalSubjectValue,
              compValue: compValue,
              subjectNumeric: subjectNumeric,
              compNumeric: compNumeric,
              difference: numericDifference,
              adjustment: adjustment
            });
          } else {
            logger.log(`✅ ${key} ADJUSTMENT (PERCENTAGE-BASED):`, {
              subjectValue: finalSubjectValue,
              compValue: compValue,
              subjectNumeric: subjectNumeric,
              compNumeric: compNumeric,
              difference: numericDifference,
              adjustment: adjustment,
              totalRawAdjustment: totalRawAdjustment
            });
          }
        }
        
        if (includeTooltipData && adjustment !== 0) {
          const label = key === 'ConditionIn' ? 'Condition' : 'Grade';
          rawAdjustments[label] = adjustment;
        }
        return;
      }
      
      // Normalize values for fields that should treat nulls as 0
      let normalizedSubjectValue = finalSubjectValue;
      let normalizedCompValue = compValue;
      
      // For fields that should be normalized to 0 when blank (boolean-like fields)
      if (shouldNormalizeToZero(key)) {
        // Normalize subject value
        normalizedSubjectValue = finalSubjectValue === '' || finalSubjectValue === null || finalSubjectValue === undefined ? 0 : finalSubjectValue;
        
        // Normalize comp value - handle "True"/"False" strings
        if (compValue === 'True' || compValue === true) {
          normalizedCompValue = 1;
        } else if (compValue === 'False' || compValue === false) {
          normalizedCompValue = 0;
        } else {
          normalizedCompValue = compValue === '' || compValue === null || compValue === undefined ? 0 : compValue;
        }

      } else {
        // Skip if values are not valid for calculation (but only for fields that don't normalize nulls)
        if (finalSubjectValue === '' || compValue === '') {
          if (shouldLog) {
            logger.log(`❌ SKIPPING ${key}: empty values - subjectValue: "${finalSubjectValue}", compValue: "${compValue}"`);
          }
          return;
        }
      }
      
      // Display rows store features as True/False/None; the comp sheet stores 1/0.
      const asBooleanNumber = (value) => {
        if (value === true || value === 'True' || value === 'true') return 1;
        if (value === false || value === 'False' || value === 'false' || value === 'None') return 0;
        return value;
      };
      const subjectForNumber = asBooleanNumber(normalizedSubjectValue);
      const compForNumber = asBooleanNumber(normalizedCompValue);
      const numericSubjectValue = isNaN(parseFloat(subjectForNumber)) ? 0 : parseFloat(subjectForNumber);
      const numericCompValue = isNaN(parseFloat(compForNumber)) ? 0 : parseFloat(compForNumber);
      
      
      // Apply proper adjustment factor
      let actualAdjustmentFactor = adjustmentFactor;
      if (key === 'Sqft' && adjustments.Sqft === "IFMV Linked") {
        const subjectIFMV = getSubjectValue('IFMV');
        if (subjectIFMV) {
          actualAdjustmentFactor = subjectIFMV / 10000;
        }
      }
      
      // Ensure the adjustment factor is a valid number
      const numericAdjustmentFactor = isNaN(parseFloat(actualAdjustmentFactor)) ? 0 : parseFloat(actualAdjustmentFactor);
      
      const difference = numericSubjectValue - numericCompValue;
      const adjustment = Math.round(difference * numericAdjustmentFactor);
      totalRawAdjustment += adjustment;
      
      if (shouldLog) {
        if (adjustment === 0) {
          logger.log(`⚪ ${key} ADJUSTMENT (ZERO):`, {
            subjectValue: finalSubjectValue,
            compValue: compValue,
            difference: difference,
            adjustment: adjustment
          });
        } else {
          logger.log(`✅ ${key} ADJUSTMENT:`, {
            subjectValue: finalSubjectValue,
            compValue: compValue,
            difference: difference,
            adjustment: adjustment,
            totalRawAdjustment: totalRawAdjustment
          });
        }
      }
      

      
      if (includeTooltipData && adjustment !== 0) {
        // Format label nicely
        const label = key
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        rawAdjustments[label] = adjustment;
      }
    });
  }

  let totalTimeAdjustment = 0;
  const monthsRaw =
    timeAdjustmentMonths !== undefined
      ? timeAdjustmentMonths
      : usePersistedDataStore.getState().negotiationObj?.timeAdjustmentMonths;
  const months = Math.floor(Number(monthsRaw));
  const shouldUseTimeAdjustedPrice = Number.isFinite(months) && months >= 1;

  if (shouldUseTimeAdjustedPrice) {
    const timeAdjRaw = getCompValue('TimeAdjSalePrice');
    const timeAdjFallback = getCompValue('time_adj');
    const timeAdjSource =
      timeAdjRaw !== undefined && timeAdjRaw !== null && timeAdjRaw !== ''
        ? timeAdjRaw
        : timeAdjFallback;
    const timeAdjSalePrice = parseFloat(timeAdjSource);
    const effectiveSalePrice = parseFloat(salePrice ?? baseValue);

    if (Number.isFinite(timeAdjSalePrice) && Number.isFinite(effectiveSalePrice)) {
      const timeAdjustment = Math.round(timeAdjSalePrice - effectiveSalePrice);
      if (timeAdjustment !== 0) {
        totalTimeAdjustment = timeAdjustment;
        if (includeTooltipData) {
          rawAdjustments['Time Adjustment'] = timeAdjustment;
        }
      }
    }
  }

  // Calculate misc adjustments (only if not excluded)
  if (shouldLog) {
    logger.log('\nStarting misc adjustments calculation...');
    logger.log('miscAdjustments:', miscAdjustments);
    logger.log('currentCompId:', currentCompId);
    logger.log('savedComps:', savedComps);
    logger.log('excludeMiscAdjustments:', excludeMiscAdjustments);
  }
  
  if (!excludeMiscAdjustments && miscAdjustments && currentCompId && savedComps.length > 0) {
    Object.entries(miscAdjustments).forEach(([adjKey, adj]) => {
      if (shouldLog) {
        logger.log(`\n--- Processing misc adjustment: ${adjKey} ---`);
        logger.log('adj:', adj);
      }
      
      const subjectAdjustmentFlag = adj.Values[0] || 0;
      const needle = normalizeCompId(currentCompId);
      const compIndex = savedComps.findIndex((id) => normalizeCompId(id) === needle);
      const compAdjustmentFlag = adj.Values[compIndex + 1] || 0;
      const adjustmentValue = Number(adj.Adjustment) || 0;
      const adjustmentType = adj.AdjustmentType;
      

      
      const flagDifference = compAdjustmentFlag - subjectAdjustmentFlag;
      let adjustment = 0;
      
      if (adjustmentType === 'percentage') {
        adjustment = (adjustmentValue / 100) * baseValue * flagDifference;
      } else {
        adjustment = adjustmentValue * flagDifference;
      }
      
      totalMiscAdjustment += adjustment;
      
      if (shouldLog) {
        logger.log(`${adjKey} MISC ADJUSTMENT:`, {
          subjectFlag: subjectAdjustmentFlag,
          compFlag: compAdjustmentFlag,
          flagDifference: flagDifference,
          adjustmentValue: adjustmentValue,
          adjustmentType: adjustmentType,
          baseValue: baseValue,
          adjustment: adjustment,
          totalMiscAdjustment: totalMiscAdjustment
        });
      }
      
      if (includeTooltipData && adjustment !== 0) {
        let displayAdjustment = adjustmentValue;
        if (adjustmentType === 'percentage') {
          displayAdjustment = `${adjustmentValue}% (${Math.round(adjustment).toLocaleString()})`;
        } else {
          displayAdjustment = `$${Math.round(adjustment).toLocaleString()}`;
        }
        
        miscAdjustmentTooltips.push({
          key: adjKey,
          name: adj.Name,
          type: adjustmentType,
          adjustmentValue: flagDifference < 0 ? `-${displayAdjustment}` : displayAdjustment,
          adjustment,
          parcelId: currentCompId,
        });
      }
    });
  }

  const finalAdjustedValue = baseValue + totalRawAdjustment + totalMiscAdjustment + totalTimeAdjustment;
  
  if (shouldLog) {
    logger.log('\n=== FINAL CALCULATION SUMMARY ===');
    logger.log('Parcel ID:', currentCompId);
    logger.log('Base Sale Price:', baseValue);
    logger.log('Total Raw Adjustments:', totalRawAdjustment);
    logger.log('Total Misc Adjustments:', totalMiscAdjustment);
    logger.log('Total Time Adjustment:', totalTimeAdjustment);
    logger.log('Final Adjusted Value:', finalAdjustedValue);
    logger.log('Total Adjustment Amount:', totalRawAdjustment + totalMiscAdjustment + totalTimeAdjustment);
    logger.log('Adjustment Percentage:', ((totalRawAdjustment + totalMiscAdjustment + totalTimeAdjustment) / baseValue * 100).toFixed(2) + '%');
    logger.log(`=== END ADJUSTMENT CALCULATOR DEBUG (${logType}) ===\n`);
  }

  if (includeTooltipData) {
    return {
      baseValue,
      totalRawAdjustment,
      totalMiscAdjustment,
      totalTimeAdjustment,
      finalAdjustedValue,
      rawAdjustments,
      miscAdjustmentTooltips,
      totalAdjustments: totalRawAdjustment + totalMiscAdjustment + totalTimeAdjustment
    };
  }

  return finalAdjustedValue;
}

/**
 * Simplified version that just returns the final adjusted price
 * Useful for cases where you only need the final value
 */
export function getCompAdjustedPrice(params) {
  return calculateCompAdjustedPrice(params);
}

/**
 * Version that returns detailed breakdown for tooltips
 * Used in CaseReview renderCell for displaying adjustment details
 */
export function getCompAdjustedPriceWithTooltip(params) {
  return calculateCompAdjustedPrice({
    ...params,
    includeTooltipData: true
  });
}

